<template>
  <div>
    <passkey-setup-dialog
      v-model="show"
      :required="required"
      :email="email"
      :prf-salt-b64="prfSaltB64"
      :intro="intro"
      @enrolled="onEnrolled"
      @skipped="onSkipped"
    />
    <v-snackbar v-model="showToast" app location="top" :timeout="6000">
      <v-icon :color="toastError ? 'error' : 'accent'" class="pr-3">{{ toastError ? 'error_outline' : 'vpn_key' }}</v-icon>
      <span>{{ toastText }}</span>
      <template v-slot:actions>
        <v-btn icon variant="text" size="small" @click="showToast = false"><v-icon>close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
  // The one host of the post-unlock passkey offer (services/e2ee/nc1/
  // PasskeyOffer.js). Mounted ONCE in App.vue next to Nc1UnlockDialog, so a
  // request from any route — a dashboard tile's unlock dialog, the add-device
  // approval, the Share-encryption-keys page, App.vue's pending-link watch —
  // reaches it, and the offer outlives whatever page asked for it. The
  // WebAuthn ceremony itself runs in PasskeySetupDialog's button handler (a
  // real user gesture); this component only decides whether to ask and
  // publishes the wrap.
  import { enrollPasskeyWrap } from '@/services/e2ee/nc1/SignupBootstrap'
  import { Nc1PasskeyOffer, passkeyOfferEligible } from '@/services/e2ee/nc1/PasskeyOffer'
  import PasskeySetupDialog from '@/components/e2ee/PasskeySetupDialog'

  const INTROS = {
    recovery: 'You unlocked this browser with your recovery code. Set up a passkey on this browser and you won\'t '
      + 'need to enter the recovery code here again: your device\'s screen lock (Windows Hello, Touch ID or similar) '
      + 'will unlock your encrypted data instead.',
    link: 'This browser just received your encryption keys from another device. Set up a passkey here so it can '
      + 'unlock your encrypted data on its own next time, without asking another device or your recovery code.'
  }

  export default {
    name: 'Nc1PasskeyOfferDialog',
    components: { PasskeySetupDialog },
    data () {
      return {
        show: false,
        required: false,
        email: '',
        prfSaltB64: '',
        intro: '',
        resolver: null,
        showToast: false,
        toastText: '',
        toastError: false
      }
    },
    beforeMount () {
      Nc1PasskeyOffer.onRequest = (email, opts) => this.run(email, opts)
    },
    beforeUnmount () {
      Nc1PasskeyOffer.onRequest = null
    },
    methods: {
      async run (email, opts = {}) {
        try {
          const eligible = await passkeyOfferEligible(email)
          if (!eligible) return
          this.email = email
          this.intro = INTROS[opts.reason] || ''
          const result = await enrollPasskeyWrap({
            email,
            cached: eligible.cached,
            required: eligible.required,
            requireAbsent: false,
            acquirePasskey: (o) => this.acquire(o)
          })
          if (result.status === 'enrolled') {
            this.toast('Passkey saved. This browser can unlock your encrypted data with it next time.')
            Nc1PasskeyOffer.notifyEnrolled(email)
          }
        } catch (e) {
          // The unlock that led here already succeeded; passkey trouble must
          // not undo that impression. The Account page can add one later.
          console.log('[nc1] post-unlock passkey enrollment failed', e)
          this.toast('The passkey could not be saved. You can add one from the Account page.', true)
        } finally {
          Nc1PasskeyOffer.resolve(email)
        }
      },
      acquire ({ required, prfSaltB64 }) {
        return new Promise((resolve) => {
          this.required = required
          this.prfSaltB64 = prfSaltB64
          this.resolver = resolve
          this.show = true
        })
      },
      onEnrolled (material) {
        if (this.resolver) {
          this.resolver(material)
          this.resolver = null
        }
      },
      onSkipped () {
        if (this.resolver) {
          this.resolver(null)
          this.resolver = null
        }
      },
      toast (text, error = false) {
        this.toastText = text
        this.toastError = error
        this.showToast = true
      }
    }
  }
</script>
